import { audio } from '../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../state/SaveManager.js';
import { NUMBER_KINGDOM_ASSETS } from '../NumberKingdomAssets.js';
import {
  calculateChallengeSafeBounds,
  isCompactChallengeLayout,
  type ChallengeSafeBounds,
} from './NumberKingdomChallengeLayout.js';
import type { NumberCoreChallengeConfig } from './NumberCoreChallengeConfig.js';
import { NumberCoreChallengeState, type NumberCorePlace } from './NumberCoreChallengeState.js';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';
const PANEL_SOURCE_WIDTH = 1672;
const PANEL_SOURCE_HEIGHT = 941;

interface NumberCoreCallbacks {
  onCancel: () => void;
  onComplete: (config: NumberCoreChallengeConfig) => void;
}

interface NumberCoreLayout {
  compact: boolean;
  safe: ChallengeSafeBounds;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
  machineX: number;
  machineY: number;
  machineWidth: number;
  machineHeight: number;
  controlY: number;
  controlWidth: number;
  controlHeight: number;
  feedbackY: number;
}

interface PlaceControl {
  place: NumberCorePlace;
  image: Phaser.GameObjects.Image;
  plusZone: Phaser.GameObjects.Zone;
  minusZone: Phaser.GameObjects.Zone;
}

export class NumberCoreChallenge {
  private readonly state: NumberCoreChallengeState;
  private readonly container: Phaser.GameObjects.Container;
  private readonly controls: PlaceControl[] = [];
  private readonly timers: Phaser.Time.TimerEvent[] = [];
  private panel!: Phaser.GameObjects.Image;
  private backButton!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private targetText!: Phaser.GameObjects.Text;
  private digitTexts!: Record<NumberCorePlace, Phaser.GameObjects.Text>;
  private decompositionText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private machine!: Phaser.GameObjects.Image;
  private energyCrystal!: Phaser.GameObjects.Image;
  private errorPulse!: Phaser.GameObjects.Image;
  private repairBurst!: Phaser.GameObjects.Image;
  private repairButton!: Phaser.GameObjects.Zone;
  private hintButton!: Phaser.GameObjects.Zone;
  private resetButton!: Phaser.GameObjects.Zone;
  private currentLayout!: NumberCoreLayout;
  private hintIndex = 0;
  private inputLocked = false;
  private suspended = false;
  private destroyed = false;

  constructor(
    private readonly scene: Phaser.Scene,
    readonly config: NumberCoreChallengeConfig,
    private readonly reducedMotion: boolean,
    private readonly callbacks: NumberCoreCallbacks,
  ) {
    this.state = new NumberCoreChallengeState(config);
    this.container = scene.add.container(0, 0).setDepth(220).setAlpha(0);
    this.createInterface();
    this.registerKeyboard();
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: this.reducedMotion ? 150 : 310,
      ease: 'Sine.easeOut',
    });
  }

  private createInterface() {
    this.panel = this.scene.add.image(640, 170, NUMBER_KINGDOM_ASSETS.numberCoreChallengePanel.key);

    this.backButton = this.scene.add.text(205, 22, '←  BACK TO VALLEY', {
      fontFamily: FONT_UI,
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#fff8dd',
      stroke: '#17351f',
      strokeThickness: 5,
      padding: { x: 10, y: 8 },
    }).setOrigin(0.5, 0).setInteractive({ useHandCursor: true });
    this.backButton.on('pointerup', () => this.requestCancel());

    this.controlsText = this.scene.add.text(640, 22,
      'Keyboard: 1 / 2 / 3 add  •  Shift + number removes  •  Enter repair  •  H hint  •  R reset', {
        fontFamily: FONT_UI,
        fontSize: '12px',
        fontStyle: 'bold',
        color: '#e7f7ff',
        stroke: '#10245d',
        strokeThickness: 4,
      }).setOrigin(0.5, 0).setVisible(false);

    const fieldStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: FONT_UI,
      fontSize: '38px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#071c62',
      strokeThickness: 7,
    };
    this.targetText = this.scene.add.text(570, 210, String(this.config.target), {
      ...fieldStyle,
      fontSize: '42px',
      color: '#ffe96b',
    }).setOrigin(0.5);
    this.digitTexts = {
      hundreds: this.scene.add.text(720, 210, '', fieldStyle).setOrigin(0.5),
      tens: this.scene.add.text(800, 210, '', fieldStyle).setOrigin(0.5),
      ones: this.scene.add.text(880, 210, '', fieldStyle).setOrigin(0.5),
    };

    this.machine = this.scene.add.image(220, 445, NUMBER_KINGDOM_ASSETS.numberCoreBroken.key);
    this.machine.setCrop(30, 0, 300, 350);
    this.energyCrystal = this.scene.add.image(220, 420, NUMBER_KINGDOM_ASSETS.coreEnergyCrystal.key)
      .setVisible(false).setAlpha(0);
    this.energyCrystal.setCrop(20, 0, 178, 287);
    this.errorPulse = this.scene.add.image(220, 435, NUMBER_KINGDOM_ASSETS.coreErrorPulse.key)
      .setVisible(false).setAlpha(0);
    this.errorPulse.setCrop(0, 0, 290, 264);
    this.repairBurst = this.scene.add.image(220, 435, NUMBER_KINGDOM_ASSETS.coreRepairBurst.key)
      .setVisible(false).setAlpha(0);
    this.repairBurst.setCrop(0, 0, 313, 265);

    this.createPlaceControl('hundreds', NUMBER_KINGDOM_ASSETS.repairHundredControl.key);
    this.createPlaceControl('tens', NUMBER_KINGDOM_ASSETS.repairTenControl.key);
    this.createPlaceControl('ones', NUMBER_KINGDOM_ASSETS.repairOneControl.key);

    this.decompositionText = this.scene.add.text(640, 554, '', {
      fontFamily: FONT_UI,
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#fff8cf',
      stroke: '#17351f',
      strokeThickness: 5,
    }).setOrigin(0.5);
    this.feedbackText = this.scene.add.text(640, 586, 'Inspect the machine and repair what does not match.', {
      fontFamily: FONT_UI,
      fontSize: '16px',
      fontStyle: 'bold',
      align: 'center',
      color: '#fff2a6',
      stroke: '#17351f',
      strokeThickness: 5,
      wordWrap: { width: 760 },
    }).setOrigin(0.5);

    this.repairButton = this.createButtonZone(() => this.submitRepair());
    this.hintButton = this.createButtonZone(() => this.showHint());
    this.resetButton = this.createButtonZone(() => this.resetCore());

    this.container.add([
      this.panel, this.backButton, this.controlsText, this.targetText,
      this.digitTexts.hundreds, this.digitTexts.tens, this.digitTexts.ones,
      this.machine, this.energyCrystal, this.errorPulse,
      this.repairBurst, this.decompositionText, this.feedbackText,
      this.repairButton, this.hintButton, this.resetButton,
    ]);
    this.layout();
    this.updateDisplay();
  }

  private createPlaceControl(place: NumberCorePlace, texture: string) {
    const image = this.scene.add.image(500, 440, texture);
    image.setCrop(0, 0, image.width, 264);
    const plusZone = this.scene.add.zone(540, 405, 64, 64).setInteractive({ useHandCursor: true });
    const minusZone = this.scene.add.zone(540, 470, 64, 64).setInteractive({ useHandCursor: true });
    plusZone.on('pointerup', () => this.adjust(place, 1));
    minusZone.on('pointerup', () => this.adjust(place, -1));
    this.controls.push({ place, image, plusZone, minusZone });
    this.container.add([image, plusZone, minusZone]);
  }

  private createButtonZone(callback: () => void) {
    const zone = this.scene.add.zone(640, 660, 120, 60).setInteractive({ useHandCursor: true });
    zone.on('pointerup', callback);
    return zone;
  }

  layout() {
    if (this.destroyed || !this.panel) return;
    const safe = calculateChallengeSafeBounds(this.scene);
    const compact = isCompactChallengeLayout(this.scene, safe);
    const panelWidth = Math.min(compact ? 850 : 940, safe.width - 18);
    const panelHeight = panelWidth * (PANEL_SOURCE_HEIGHT / PANEL_SOURCE_WIDTH);
    const panelBottom = safe.y + panelHeight + (compact ? 2 : 4);
    const layout: NumberCoreLayout = {
      compact,
      safe,
      panelY: safe.y + panelHeight * 0.5 + (compact ? 2 : 4),
      panelWidth,
      panelHeight,
      machineX: safe.x + (compact ? 88 : 100),
      machineY: safe.bottom - (compact ? 76 : 84),
      machineWidth: compact ? 130 : 148,
      machineHeight: compact ? 136 : 154,
      controlY: safe.bottom - (compact ? 70 : 78),
      controlWidth: compact ? 124 : 138,
      controlHeight: compact ? 117 : 130,
      feedbackY: panelBottom + (compact ? 35 : 39),
    };
    this.currentLayout = layout;

    this.panel.setPosition(safe.centerX, layout.panelY).setDisplaySize(layout.panelWidth, layout.panelHeight);
    this.backButton.setPosition(safe.x + (compact ? 72 : 82), safe.y + 2)
      .setFontSize(compact ? 11 : 13);
    this.controlsText.setPosition(safe.centerX, safe.y + 2).setFontSize(compact ? 9 : 11);

    const panelScale = layout.panelWidth / PANEL_SOURCE_WIDTH;
    const panelLeft = safe.centerX - layout.panelWidth * 0.5;
    const panelTop = layout.panelY - layout.panelHeight * 0.5;
    const placeText = (text: Phaser.GameObjects.Text, sourceX: number, sourceY: number, fontSize: number) => {
      text.setPosition(panelLeft + sourceX * panelScale, panelTop + sourceY * panelScale)
        .setFontSize(Math.round(fontSize * panelScale));
    };
    placeText(this.targetText, 586, 458, 94);
    placeText(this.digitTexts.hundreds, 885, 477, 86);
    placeText(this.digitTexts.tens, 1104, 477, 86);
    placeText(this.digitTexts.ones, 1322, 477, 86);

    const placeButtonZone = (
      zone: Phaser.GameObjects.Zone,
      sourceX: number,
      sourceY: number,
      sourceWidth: number,
      sourceHeight: number,
    ) => {
      zone.setPosition(panelLeft + sourceX * panelScale, panelTop + sourceY * panelScale)
        .setSize(sourceWidth * panelScale, sourceHeight * panelScale);
    };
    placeButtonZone(this.resetButton, 447, 805, 310, 122);
    placeButtonZone(this.repairButton, 837, 793, 370, 142);
    placeButtonZone(this.hintButton, 1245, 805, 310, 122);

    this.machine.setPosition(layout.machineX, layout.machineY)
      .setDisplaySize(layout.machineWidth, layout.machineHeight);
    this.energyCrystal.setPosition(layout.machineX, layout.machineY - (compact ? 18 : 22))
      .setDisplaySize(compact ? 90 : 105, compact ? 129 : 151);
    this.errorPulse.setPosition(layout.machineX, layout.machineY)
      .setDisplaySize(compact ? 185 : 215, compact ? 182 : 211);
    this.repairBurst.setPosition(layout.machineX, layout.machineY)
      .setDisplaySize(compact ? 195 : 225, compact ? 181 : 208);

    const controlGap = compact ? 190 : 210;
    this.controls.forEach((control, index) => {
      const x = safe.centerX + (index - 1) * controlGap + (compact ? 42 : 54);
      control.image.setPosition(x, layout.controlY)
        .setDisplaySize(layout.controlWidth, layout.controlHeight);
      const zoneX = x + layout.controlWidth * 0.24;
      control.plusZone.setPosition(zoneX, layout.controlY - layout.controlHeight * 0.24)
        .setSize(layout.controlWidth * 0.46, layout.controlHeight * 0.48);
      control.minusZone.setPosition(zoneX, layout.controlY + layout.controlHeight * 0.24)
        .setSize(layout.controlWidth * 0.46, layout.controlHeight * 0.48);
    });

    this.decompositionText.setPosition(safe.centerX + (compact ? 42 : 54), layout.feedbackY - 20)
      .setFontSize(compact ? 14 : 16);
    this.feedbackText.setPosition(safe.centerX + (compact ? 42 : 54), layout.feedbackY)
      .setFontSize(compact ? 12 : 14)
      .setWordWrapWidth(Math.min(720, safe.width - 330));
  }

  private adjust(place: NumberCorePlace, delta: -1 | 1) {
    if (!this.canInteract()) return;
    if (!this.state.adjust(place, delta)) {
      audio.hint();
      this.feedbackText.setText(`${this.labelFor(place)} must stay between 0 and 9.`);
      return;
    }
    audio.click();
    this.feedbackText.setText('The core updated. Compare it with the target.');
    this.updateDisplay();
  }

  private updateDisplay() {
    this.digitTexts.hundreds.setText(String(this.state.counts.hundreds));
    this.digitTexts.tens.setText(String(this.state.counts.tens));
    this.digitTexts.ones.setText(String(this.state.counts.ones));
    this.decompositionText.setText(this.state.decomposition);
  }

  private submitRepair() {
    if (!this.canInteract()) return;
    if (!this.state.isCorrect) {
      SaveManager.recordAnswer(this.config.skillId, false);
      audio.hint();
      this.feedbackText.setText('Something still does not match. Check each place.');
      this.scene.tweens.killTweensOf(this.errorPulse);
      this.errorPulse.setVisible(true).setAlpha(0).setScale(this.reducedMotion ? 1 : 0.9);
      this.container.bringToTop(this.errorPulse);
      this.scene.tweens.add({
        targets: this.errorPulse,
        alpha: 0.9,
        scale: 1,
        duration: this.reducedMotion ? 120 : 230,
        yoyo: true,
        hold: this.reducedMotion ? 70 : 170,
        ease: 'Sine.easeInOut',
        onComplete: () => this.errorPulse.setVisible(false),
      });
      return;
    }
    this.playSuccess();
  }

  private showHint() {
    if (!this.canInteract()) return;
    const wrongPlaces = this.state.incorrectPlaces;
    const matchingPlaces = (['hundreds', 'tens', 'ones'] as const)
      .filter(place => !wrongPlaces.includes(place));
    const matchingCopy = matchingPlaces.length > 0
      ? `${this.joinPlaceNames(matchingPlaces)} already ${matchingPlaces.length === 1 ? 'matches' : 'match'}.`
      : 'No place matches yet. Compare each digit carefully.';
    const hints = [
      'Compare the target to what the core shows.',
      matchingCopy,
      wrongPlaces.length === 1 ? `Look closely at the ${this.labelFor(wrongPlaces[0])} place.` : 'More than one place needs attention.',
      `${this.config.target} needs ${this.state.required.hundreds} hundreds, ${this.state.required.tens} tens, and ${this.state.required.ones} ones.`,
    ];
    this.feedbackText.setText(`Hint: ${hints[Math.min(this.hintIndex, hints.length - 1)]}`);
    this.hintIndex = Math.min(this.hintIndex + 1, hints.length - 1);
    audio.hint();
  }

  private resetCore() {
    if (!this.canInteract()) return;
    this.state.reset();
    audio.click();
    this.feedbackText.setText(`Core reset to its broken value: ${this.state.value}.`);
    this.updateDisplay();
  }

  private playSuccess() {
    this.inputLocked = true;
    SaveManager.recordAnswer(this.config.skillId, true);
    audio.correct();
    this.feedbackText.setText(`The Number Core now shows ${this.config.target}!`);
    this.machine.setTexture(NUMBER_KINGDOM_ASSETS.numberCorePowered.key)
      .setCrop(80, 0, 305, 350);
    this.layout();
    this.repairBurst.setVisible(true).setAlpha(0).setScale(this.reducedMotion ? 1 : 0.82);
    this.energyCrystal.setVisible(true).setAlpha(0).setScale(this.reducedMotion ? 1 : 0.84);
    this.container.bringToTop(this.repairBurst);
    this.container.bringToTop(this.energyCrystal);
    this.scene.tweens.add({
      targets: this.repairBurst,
      alpha: 1,
      scale: 1,
      duration: this.reducedMotion ? 170 : 430,
      ease: 'Back.easeOut',
    });
    this.scene.tweens.add({
      targets: this.energyCrystal,
      alpha: 1,
      scale: 1,
      duration: this.reducedMotion ? 190 : 520,
      ease: 'Sine.easeOut',
    });
    this.timers.push(this.scene.time.delayedCall(this.reducedMotion ? 720 : 1300, () => {
      const config = this.config;
      this.destroy();
      this.callbacks.onComplete(config);
    }));
  }

  private registerKeyboard() {
    this.scene.input.keyboard?.on('keydown', this.handleKeyDown, this);
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (!this.canInteract()) return;
    this.controlsText.setVisible(true);
    const placeByCode: Partial<Record<string, NumberCorePlace>> = {
      Digit1: 'hundreds', Numpad1: 'hundreds',
      Digit2: 'tens', Numpad2: 'tens',
      Digit3: 'ones', Numpad3: 'ones',
    };
    const place = placeByCode[event.code];
    if (place) {
      event.preventDefault();
      this.adjust(place, event.shiftKey ? -1 : 1);
    } else if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      event.preventDefault();
      this.submitRepair();
    } else if (event.code === 'KeyH') {
      event.preventDefault();
      this.showHint();
    } else if (event.code === 'KeyR') {
      event.preventDefault();
      this.resetCore();
    } else if (event.code === 'Escape') {
      event.preventDefault();
      this.requestCancel();
    }
  }

  private labelFor(place: NumberCorePlace) {
    return place === 'hundreds' ? 'hundreds' : place === 'tens' ? 'tens' : 'ones';
  }

  private joinPlaceNames(places: readonly NumberCorePlace[]) {
    const names = places.map(place => `the ${this.labelFor(place)} place`);
    if (names.length < 2) return names[0] ?? '';
    return names.length === 2 ? `${names[0]} and ${names[1]}` : `${names.slice(0, -1).join(', ')}, and ${names.at(-1)}`;
  }

  private requestCancel() {
    if (this.destroyed || this.inputLocked) return;
    this.destroy();
    this.callbacks.onCancel();
  }

  setSuspended(suspended: boolean) { this.suspended = suspended; }

  private canInteract() {
    return !this.destroyed && !this.inputLocked && !this.suspended;
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.timers.forEach(timer => timer.remove(false));
    this.timers.length = 0;
    this.scene.tweens.killTweensOf(this.container.getAll());
    this.scene.tweens.killTweensOf(this.container);
    this.container.destroy(true);
  }
}
